import { Model, Factory, Response, belongsTo, hasMany, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  rand,
  randPastDate,
  randRecentDate,
  randNumber,
  randChanceBoolean,
  randProductName,
  randPhrase,
} from '@ngneat/falso'
import type { MockServer, MirageServer } from '../server.js'
import type { SchemaAttributes } from '../../types.js'

import { pageable, sortable, getExistingEntity } from '../utils.js'

type ConsultationItemOverviewAttributes =
  SchemaAttributes<'ConsultationItemOverview'>

export const model = (
  Model as ModelDefinition<ConsultationItemOverviewAttributes>
).extend({
  consultation: belongsTo(),
  patient: belongsTo(),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
  supervisingVeterinarian: belongsTo('user'),
  template: belongsTo('consultationItemTemplate'),
  healthPlan: belongsTo('patientHealthPlan'),
  healthPlanItem: belongsTo('patientHealthPlanItem'),
  linkedItems: hasMany('simpleConsultationItem'),
  siblingItems: hasMany('simpleConsultationItem'),
  parentLinkedItem: belongsTo('simpleConsultationItem'),
  compensatedItem: belongsTo('patientHealthPlanItem'),
  handlingFeeReferral: belongsTo('consultationLaboratoryReferral'),
  urgentFeeReferral: belongsTo('consultationLaboratoryReferral'),
})

export const factory = Factory.extend<ConsultationItemOverviewAttributes>({
  code() {
    const typeCode = rand(['PROC', 'MED', 'FOOD', 'SUPPLY'])
    return typeCode
      .concat('-')
      .concat(String(randNumber({ min: 1000, max: 9999 })))
  },
  name() {
    return randProductName()
  },
  type_code() {
    return rand(['PROCEDURE', 'FOOD', 'SUPPLY', 'MEDICINE'])
  },
  usage_type() {
    return rand(['ADMINISTERED', 'PRESCRIPTION', 'GIVEN', 'HOME_DELIVERY'])
  },
  used() {
    return randChanceBoolean({ chanceTrue: 0.8 })
      ? randRecentDate().toISOString()
      : null
  },
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randRecentDate().toISOString()
  },
  herd_size_multiplier() {
    return randChanceBoolean({ chanceTrue: 0.3 })
      ? randNumber({ min: 1, max: 10 })
      : null
  },
  display_price() {
    return randNumber({ min: 10, max: 500, precision: 2 })
  },
  hidden_items_display_price() {
    return randChanceBoolean({ chanceTrue: 0.2 })
      ? randNumber({ min: 5, max: 50, precision: 2 })
      : 0
  },
  is_minimum_charge_active() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  display_amount() {
    return randNumber({ min: 1, max: 10, precision: 1 })
  },
  display_size() {
    const sizes = ['mg', 'box', 'bottle', 'ml']
    return rand(sizes)
  },
  active_substance() {
    const substances = [
      'Amoxicillin',
      'Meloxicam',
      'Prednisolone',
      'Metronidazole',
      'Cephalexin',
    ]
    return randChanceBoolean({ chanceTrue: 0.8 }) ? rand(substances) : null
  },
  description() {
    return randChanceBoolean({ chanceTrue: 0.7 }) ? randPhrase() : null
  },

  // Traits for relationships (icons)
  withTemplate: trait({
    afterCreate(item, server) {
      item.update({
        template: getExistingEntity(server, 'consultationItemTemplate'),
      })
    },
  }),

  withHealthPlanItem: trait({
    afterCreate(item, server) {
      item.update({
        healthPlanItem: getExistingEntity(server, 'patientHealthPlanItem'),
      })
    },
  }),

  withCompensatedItem: trait({
    afterCreate(item, server) {
      item.update({
        compensatedItem: getExistingEntity(server, 'patientHealthPlanItem'),
      })
    },
  }),

  withLinkedItems: trait({
    afterCreate(item, server) {
      const allSimpleItems = server.schema.all('simpleConsultationItem').models
      const minRequired = 3
      const missingCount = Math.max(0, minRequired - allSimpleItems.length)
      if (missingCount > 0) {
        server.createList('simpleConsultationItem', missingCount)
      }
      const updatedSimpleItems = server.schema.all(
        'simpleConsultationItem',
      ).models
      if (updatedSimpleItems.length > 0) {
        const count = randNumber({
          min: 0,
          max: Math.min(3, updatedSimpleItems.length),
        })
        const selectedItems: typeof updatedSimpleItems = []
        const available = [...updatedSimpleItems]
        for (let i = 0; i < count; i++) {
          const randomItem = rand(available)
          available.splice(available.indexOf(randomItem), 1)
          selectedItems.push(randomItem)
        }
        item.update({ linkedItems: selectedItems })
      }
    },
  }),

  withSiblingItems: trait({
    afterCreate(item, server) {
      const allSimpleItems = server.schema.all('simpleConsultationItem').models
      const minRequired = 3
      const missingCount = Math.max(0, minRequired - allSimpleItems.length)
      if (missingCount > 0) {
        server.createList('simpleConsultationItem', missingCount)
      }
      const updatedSimpleItems = server.schema.all(
        'simpleConsultationItem',
      ).models
      if (updatedSimpleItems.length > 0) {
        const count = randNumber({
          min: 0,
          max: Math.min(2, updatedSimpleItems.length),
        })
        const selectedItems: typeof updatedSimpleItems = []
        const available = [...updatedSimpleItems]
        for (let i = 0; i < count; i++) {
          const randomItem = rand(available)
          available.splice(available.indexOf(randomItem), 1)
          selectedItems.push(randomItem)
        }
        item.update({ siblingItems: selectedItems })
      }
    },
  }),

  withParentLinkedItem: trait({
    afterCreate(item, server) {
      item.update({
        parentLinkedItem: getExistingEntity(server, 'simpleConsultationItem'),
      })
    },
  }),

  afterCreate(item, server) {
    if (!item.consultation) {
      const consultation = getExistingEntity(server, 'consultation')
      item.update({ consultation })
    }

    if (!item.patient && item.consultation?.patients?.models?.length > 0) {
      item.update({ patient: item.consultation.patients.models[0] })
    }

    if (!item.createdUser) {
      const createdUser = getExistingEntity(server, 'user')
      item.update({ createdUser })
    }

    if (!item.modifiedUser) {
      const modifiedUser = getExistingEntity(server, 'user')
      item.update({ modifiedUser })
    }

    // Create the corresponding detail item based on type
    const stockLocation = server.schema.first('stockLocation')
    const typeCode = item.type_code

    switch (typeCode) {
      case 'FOOD':
        server.create('consultationFood', { id: item.id, stockLocation })
        break
      case 'MEDICINE':
        server.create('consultationMedicine', { id: item.id, stockLocation })
        break
      case 'PROCEDURE':
        server.create('consultationProcedure', { id: item.id })
        break
      case 'SUPPLY':
        server.create('consultationSupply', { id: item.id, stockLocation })
        break
    }
  },
})

export function routes(server: MockServer) {
  server.get(
    '/consultations/:parent_lookup_consultation/items/overview',
    function (this: MirageServer, schema, request) {
      const consultationId = request.params.parent_lookup_consultation
      const allItems = schema.all('consultationItemOverview')
      const items = allItems.filter(
        (item) => item.consultation?.id === consultationId,
      )

      const sorted = sortable(items, request, {}, ['-used'])

      const { data, meta } = pageable(sorted, request)

      return {
        ...this.serialize(data),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  server.get(
    '/consultations/:parent_lookup_consultation/price_info/',
    (schema, request) => {
      const consultationItemId = String(
        request.queryParams.consultation_item_id || '',
      )
      const organizationItemId = String(
        request.queryParams.organization_item_id || '',
      )

      if (!consultationItemId && !organizationItemId) {
        return new Response(
          400,
          {},
          {
            errors: [
              {
                detail:
                  "Provide either 'organization_item_id' or 'consultation_item_id'.",
              },
            ],
          },
        )
      }

      const priceInfoByType: Record<
        string,
        { name: string; percentage: number }
      > = {
        PROCEDURE: { name: 'Standard clinic rate', percentage: 100 },
        MEDICINE: { name: 'Pharmacy rate', percentage: 75 },
        FOOD: { name: 'Standard rate', percentage: 50 },
        SUPPLY: { name: 'Bulk rate', percentage: 125 },
      }

      let typeCode = 'PROCEDURE'
      if (consultationItemId) {
        const item = schema.find('consultationItemOverview', consultationItemId)
        if (!item) {
          return new Response(
            404,
            {},
            { errors: [{ detail: 'Consultation item not found.' }] },
          )
        }
        typeCode = (item.type_code as string) || 'PROCEDURE'
      }

      const { name, percentage } =
        priceInfoByType[typeCode] || priceInfoByType.PROCEDURE

      return {
        data: {
          type: 'ItemPriceInfo',
          id: request.params.parent_lookup_consultation,
          attributes: {
            item_type: typeCode,
            preference_mode: 'consultation',
            department_rate: {
              name,
              percentage,
              rate_id: 1,
            },
          },
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  const consultationIds = ['1', '2']

  consultationIds.forEach((id) => {
    const consultation = server.schema.find('consultation', id)

    if (consultation) {
      for (let i = 0; i < 8; i++) {
        const traits: string[] = []

        // Only one of healthPlanItem, compensatedItem, or template
        const relationshipType = rand([
          'withHealthPlanItem',
          'withCompensatedItem',
          'withTemplate',
          null,
        ])
        if (relationshipType) {
          traits.push(relationshipType)
        }

        // Only one of these three self-referential relationships, or none
        const linkRelationshipType = rand([
          'withLinkedItems',
          'withSiblingItems',
          'withParentLinkedItem',
          null,
        ])
        if (linkRelationshipType) {
          traits.push(linkRelationshipType)
        }

        server.create('consultationItemOverview', ...traits, {
          consultation,
          patient: consultation.patients?.models?.[0] || null,
        })
      }
    }
  })
}
